import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { CompareViewRoute } from "../../types/router";
import { useRetrieveDesignElementById } from "../useRetrieveDesignElementById";
import { useRetrieveDesignElementVersions } from "../useRetrieveDesignElementVersions";
import {
  findDesignElementVersionByLabel,
  isNewerDesignElementVersion,
} from "../../domain/designElementVersion";

export const useCompareView = () => {
  const { selectedItemId, baseVersion } =
    useParams<CompareViewRoute>() as CompareViewRoute;
  const [searchParams] = useSearchParams();
  const viewedLabel = searchParams.get("version");

  const versions = useRetrieveDesignElementVersions(selectedItemId);
  const items = useMemo(() => versions.data ?? [], [versions.data]);

  const older = useMemo(
    () => findDesignElementVersionByLabel(items, baseVersion),
    [items, baseVersion],
  );

  const viewed = useMemo(
    () =>
      viewedLabel
        ? findDesignElementVersionByLabel(items, viewedLabel)
        : items.find((item) => item.isLatest),
    [items, viewedLabel],
  );

  const olderElement = useRetrieveDesignElementById(
    selectedItemId,
    older?.version,
    !!older,
  );
  const viewedElement = useRetrieveDesignElementById(
    selectedItemId,
    viewed?.version,
    !!viewed,
  );

  const isValidComparison =
    !!older && !!viewed && isNewerDesignElementVersion(viewed, older);

  return {
    isLoading:
      versions.isLoading || olderElement.isLoading || viewedElement.isLoading,
    isNotFound: !versions.isLoading && !isValidComparison,
    olderElement: olderElement.data,
    viewedElement: viewedElement.data,
    older,
    viewed,
    versions,
  };
};
